import { chunk, filter, find, map, some } from 'es-toolkit/compat';
import pLimit from 'p-limit';

import { getCellTableColumnMappings } from 'api/study';
import { CellTableColumnIssue, CellTableColumnMappingSource } from 'interfaces/cellTableColumnMappings';
import { CellTableArtifactFile } from 'components/StudiesDashboard/StudyExportMenu/cellTableArtifactDiscovery';
import { describeExportFailure } from './exportErrorReport';
import {
  createRemoteParquetSource,
  getParquetColumnNames,
  ParquetColumnRenameError,
  rewriteParquetColumnNames,
} from './parquetColumnRename/rewriteParquetColumnNames';

export const FORMATTED_CELL_TABLE_READ_CONCURRENCY = 5;
const MAPPING_SOURCES_PER_REQUEST = 50;

export type FormattedCellTableStatus = 'ready' | 'blocked' | 'error';

export interface FormattedCellTableRename {
  rawName: string;
  outputName: string;
  probeId: string;
  displayName: string;
}

export interface FormattedCellTableEntry {
  file: CellTableArtifactFile;
  status: FormattedCellTableStatus;
  byteLength?: number;
  columnNames: string[];
  mapping?: CellTableColumnMappingSource;
  renames: FormattedCellTableRename[];
  // Why the file cannot be formatted; shown instead of a preview.
  reasons: string[];
}

export interface FormattedCellTableDependencies {
  getMappings: typeof getCellTableColumnMappings;
  createSource: typeof createRemoteParquetSource;
  readColumnNames: typeof getParquetColumnNames;
  rewrite: typeof rewriteParquetColumnNames;
}

export const defaultFormattedCellTableDependencies: FormattedCellTableDependencies = {
  getMappings: getCellTableColumnMappings,
  createSource: createRemoteParquetSource,
  readColumnNames: getParquetColumnNames,
  rewrite: rewriteParquetColumnNames,
};

const describeIssues = (issues: CellTableColumnIssue[]): string[] => {
  const seen = new Set<string>();
  return filter(
    map(issues, ({ detail }) => detail),
    (detail) => !seen.has(detail) && Boolean(seen.add(detail))
  );
};

const describeError = describeExportFailure;

const getRenames = (mapping: CellTableColumnMappingSource): FormattedCellTableRename[] =>
  mapping.columns.flatMap(({ rawName, outputName, kind, probeId, displayName }) =>
    kind === 'rna_probe' && outputName && probeId && displayName ? [{ rawName, outputName, probeId, displayName }] : []
  );

const classify = (entry: FormattedCellTableEntry, mapping: CellTableColumnMappingSource): FormattedCellTableEntry => {
  const withMapping = { ...entry, mapping, renames: getRenames(mapping) };
  if (mapping.blocked) {
    return { ...withMapping, status: 'blocked', reasons: describeIssues(mapping.issues) };
  }
  // RNA probe columns are optional: a table without any is exported with its column names unchanged.
  return { ...withMapping, status: 'ready', reasons: [] };
};

const throwIfAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('Cell-table preparation was cancelled', 'AbortError');
  }
};

interface PendingMapping {
  entry: FormattedCellTableEntry;
  resolve: (entry: FormattedCellTableEntry) => void;
  reject: (reason: unknown) => void;
}

const createMappingResolver = (studyId: string, signal: AbortSignal, dependencies: FormattedCellTableDependencies) => {
  let pending: PendingMapping[] = [];
  const resolveBatch = async (batch: PendingMapping[]) => {
    try {
      throwIfAborted(signal);
      const response = await dependencies.getMappings(
        studyId,
        map(batch, ({ entry: { file, columnNames } }) => ({
          sourceId: file.id,
          slideId: file.slideId,
          columns: columnNames,
        })),
        signal
      );
      throwIfAborted(signal);
      const mappings = new Map(map(response.sources, (source) => [source.sourceId, source]));
      batch.forEach(({ entry, resolve }) => {
        const mapping = mappings.get(entry.file.id);
        resolve(
          mapping
            ? classify(entry, mapping)
            : { ...entry, status: 'error', reasons: ['The column names could not be resolved.'] }
        );
      });
    } catch (error) {
      batch.forEach(({ entry, resolve, reject }) =>
        signal.aborted
          ? reject(error)
          : resolve({
              ...entry,
              status: 'error',
              reasons: [describeError(error, 'The column names could not be resolved.')],
            })
      );
    }
  };
  return (entry: FormattedCellTableEntry) =>
    new Promise<FormattedCellTableEntry>((resolve, reject) => {
      pending.push({ entry, resolve, reject });
      if (pending.length !== 1) return;
      // Batch footers that finish together, without waiting for a slow or not-yet-read table.
      queueMicrotask(() => {
        const batches = chunk(pending, MAPPING_SOURCES_PER_REQUEST);
        pending = [];
        batches.forEach((batch) => void resolveBatch(batch));
      });
    });
};

// Each bounded worker reads a footer and resolves its authoritative names before exposing that table.
export const loadFormattedCellTableEntries = async ({
  studyId,
  files,
  signal,
  onProgress,
  onEntry,
  dependencies = defaultFormattedCellTableDependencies,
  limit = pLimit(FORMATTED_CELL_TABLE_READ_CONCURRENCY),
}: {
  studyId: string;
  files: CellTableArtifactFile[];
  signal: AbortSignal;
  onProgress?: (progress: { readFiles: number; totalFiles: number }) => void;
  onEntry?: (entry: FormattedCellTableEntry) => void;
  dependencies?: FormattedCellTableDependencies;
  limit?: ReturnType<typeof pLimit>;
}): Promise<FormattedCellTableEntry[]> => {
  const resolveMapping = createMappingResolver(studyId, signal, dependencies);
  let readFiles = 0;

  return Promise.all(
    map(files, (file) =>
      limit(async (): Promise<FormattedCellTableEntry> => {
        throwIfAborted(signal);
        let entry: FormattedCellTableEntry;
        try {
          const { columnNames, byteLength } = await dependencies.readColumnNames(
            dependencies.createSource(file.urlForGetRequest, { headUrl: file.urlForHeadRequest }),
            signal
          );
          entry = { file, status: 'ready', columnNames, byteLength, renames: [], reasons: [] };
        } catch (error) {
          throwIfAborted(signal);
          entry = {
            file,
            status: 'error',
            columnNames: [],
            renames: [],
            reasons: [describeError(error, 'The column names could not be read from this file.')],
          };
        } finally {
          readFiles += 1;
          onProgress?.({ readFiles, totalFiles: files.length });
        }
        if (entry.status !== 'error') entry = await resolveMapping(entry);
        throwIfAborted(signal);
        onEntry?.(entry);
        return entry;
      })
    )
  );
};

export class FormattedCellTableError extends Error {
  // The Parquet reader's code, when the file itself could not be read; `source_unavailable` is an expired or broken link.
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'FormattedCellTableError';
    this.code = code;
  }
}

/**
 * Produces the formatted Parquet for one reviewed entry. The mapping is requested again with the reviewed
 * revision, so a name changed after review stops the file instead of silently producing different names.
 */
export const prepareFormattedCellTable = async ({
  studyId,
  entry,
  signal,
  dependencies = defaultFormattedCellTableDependencies,
}: {
  studyId: string;
  entry: FormattedCellTableEntry;
  signal: AbortSignal;
  dependencies?: FormattedCellTableDependencies;
}): Promise<Blob> => {
  const { file, mapping } = entry;
  if (entry.status !== 'ready' || !mapping) {
    throw new FormattedCellTableError(`${file.title} cannot be exported with formatted column names.`);
  }
  const response = await dependencies.getMappings(
    studyId,
    [
      {
        sourceId: file.id,
        slideId: file.slideId,
        columns: entry.columnNames,
        expectedMappingRevision: mapping.mappingRevision,
      },
    ],
    signal
  );
  const current = find(response.sources, { sourceId: file.id });
  if (!current) {
    throw new FormattedCellTableError(`The column names for ${file.title} could not be resolved.`);
  }
  if (some(current.issues, { code: 'stale_revision' })) {
    throw new FormattedCellTableError(
      `The column names for ${file.title} changed after you reviewed them. Reopen the export to review the new names.`
    );
  }
  if (current.blocked) {
    throw new FormattedCellTableError(
      `The column names for ${file.title} can no longer be resolved: ${describeIssues(current.issues).join(' ')}`
    );
  }
  try {
    return await dependencies.rewrite(
      dependencies.createSource(file.urlForGetRequest, { headUrl: file.urlForHeadRequest }),
      map(current.columns, ({ rawName, outputName }) => ({ rawName, outputName })),
      signal
    );
  } catch (error) {
    if (error instanceof ParquetColumnRenameError) {
      throw new FormattedCellTableError(`${file.title}: ${error.message}`, error.code);
    }
    throw error;
  }
};
